import { ApiClient } from '@listening/api-contract/api-client-tag';
import type {
  SynthesizeDocPackRange,
  SynthesizeWordTiming,
  TtsDetail,
} from '@listening/api-contract/schemas';
import { resolveVoiceInput } from '@listening/api-contract/schemas';
import { Effect } from 'effect';
import type { DocPackReadingChunk } from '../doc-pack/reading-plan';

// The player's chunk shape: a TTS detail plus the chapter it belongs to.
export type ChapterTtsChunk = TtsDetail & {
  readonly chapterId: string;
};

/**
 * A chunk as the player holds it.
 *
 * `playbackMode` is the discriminant, stamped by the two effects below — the
 * only places a chunk is constructed — so a reader narrows to the doc-pack
 * fields by checking it, never by probing the object's shape.
 */
/** Doc-pack playback has no chapters, but every log and cache record still
 * needs a chapter id. This is that id, in one place. */
export const docPackChapterId = (audioId: string) => `doc-pack:${audioId}`;

export type LegacyTtsChunk = ChapterTtsChunk & {
  readonly playbackMode: 'legacy';
};

export type DocPackTtsChunk = ChapterTtsChunk & {
  readonly playbackMode: 'doc-pack';
  readonly docPackChunk: DocPackReadingChunk;
  readonly docPackWordTimings: ReadonlyArray<SynthesizeWordTiming>;
};

export type PlaybackChunk = LegacyTtsChunk | DocPackTtsChunk;

/** Tags a chunk that reached the player by a route other than the effects
 * below — today only mobile's offline download records, which persist the
 * legacy wire shape. */
export const asLegacyTtsChunk = (chunk: ChapterTtsChunk): LegacyTtsChunk => ({
  ...chunk,
  playbackMode: 'legacy',
});

export type ChapterTtsChunkParams = {
  chapterId: string;
  ttsId: string;
  voice?: string;
};

export type SynthesizeReferenceParams = {
  audioId: string;
  docPackVersion: number;
  ranges: readonly SynthesizeDocPackRange[];
  voice?: string;
};

export type DocPackTtsChunkParams = SynthesizeReferenceParams & {
  readonly chunk: DocPackReadingChunk;
};

export const getChapterTtsChunkEffect = (params: ChapterTtsChunkParams) =>
  Effect.flatMap(ApiClient, (api) => {
    // The player stores the voice display name (e.g. "Will"), but the typed
    // client ENCODES query params through `VoiceInput`, whose encode direction
    // requires a VoiceModelId (model_id) — the lenient string→model_id decode
    // only runs server-side. Resolve to a model_id here so the request encodes.
    // (Legacy openapi-fetch sent the raw string and let the server resolve it.)
    // See packages/core/api-contract/src/schemas/voice.ts.
    const voice = params.voice ? resolveVoiceInput(params.voice) : undefined;
    return api.Tts.GetChapterTts({
      params: { chapterId: params.chapterId, ttsId: params.ttsId },
      query: voice ? { voice } : {},
    }).pipe(
      Effect.map((r): LegacyTtsChunk => ({
        ...r.tts,
        playbackMode: 'legacy',
        chapterId: params.chapterId,
      })),
    );
  }).pipe(
    Effect.withSpan('tts.chunk.load', {
      attributes: {
        'audio.chapter_id': params.chapterId,
        'tts.id': params.ttsId,
      },
    }),
  );

/** Synthesises one doc-pack reading chunk. The chapter id is synthetic — a
 * doc-pack chunk spans whatever chapters its ranges cross — so it names the
 * document instead. */
export const getDocPackTtsChunkEffect = (params: DocPackTtsChunkParams) =>
  Effect.flatMap(ApiClient, (api) => {
    const voice = params.voice ? resolveVoiceInput(params.voice) : undefined;
    return api.Tts.Synthesize({
      payload: {
        mode: 'reference',
        doc: params.audioId,
        docPackVersion: params.docPackVersion,
        ranges: [...params.ranges],
        ...(voice ? { voice } : {}),
      },
    }).pipe(
      Effect.map((r): DocPackTtsChunk => ({
        ...r.tts,
        playbackMode: 'doc-pack',
        chapterId: docPackChapterId(params.audioId),
        docPackChunk: params.chunk,
        docPackWordTimings: r.words,
      })),
    );
  }).pipe(
    Effect.withSpan('tts.synthesize.reference', {
      attributes: {
        'audio.id': params.audioId,
        'doc_pack.version': params.docPackVersion,
      },
    }),
  );
